import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  Tab,
  Tabs,
  Stack,
  Alert,
  IconButton,
  Card,
  alpha,
  useTheme,
  Chip,
  Collapse,
  CircularProgress,
  Toolbar,
  OutlinedInput,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormLabel,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  TuneRounded as TuneIcon,
  Info as InfoIcon,
  CloudQueue as CloudIcon,
  SettingsEthernet as NetworkIcon,
  DateRange as DateRangeIcon,
  Public as RegionIcon,
  Computer as ServerIcon,
  PlayArrow as ServicesIcon,
  Article as EventsIcon,
  DataUsage as DiskIcon,
  TimerOutlined,
  Settings as SettingsIcon,
  Autorenew as AutorenewIcon
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import esdlService from '../../services/esdlService';
import EsdlStatsSummary from '../../components/EsdlMonitoring/EsdlStatsSummary';
import EsdlStatusGrid from '../../components/EsdlMonitoring/EsdlStatusGrid';
import EsdlSummaryChart from '../../components/EsdlMonitoring/EsdlSummaryChart';
import ErrorLogsTable from '../../components/EsdlMonitoring/ErrorLogsTable';
import { debounce, throttle } from 'lodash';
import { useAppData } from '../../contexts/AppDataContext';
import useEsdlNotifications from '../../hooks/useEsdlNotifications';

const EsdlMonitoringDashboard = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { alerts, fetchAlerts } = useAppData();
  const [loading, setLoading] = useState({
    filters: false,
    dashboard: false,
    hosts: false,
    events: false,
    resources: false
  });
  const [error, setError] = useState(null);
  
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState(() => {
    try {
      const stored = sessionStorage.getItem('esdl-dashboard-data');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.warn('Error loading dashboard data from sessionStorage:', e);
      return null;
    }
  });
  
  const [eventStats, setEventStats] = useState(() => {
    try {
      const stored = sessionStorage.getItem('esdl-event-stats');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.warn('Error loading event stats from sessionStorage:', e);
      return null;
    }
  });
  
  const [resourceStats, setResourceStats] = useState(() => {
    try {
      const stored = sessionStorage.getItem('esdl-resource-stats');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.warn('Error loading resource stats from sessionStorage:', e);
      return null;
    }
  });

  // Auto-refresh settings
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem('esdl-auto-refresh-enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(() => {
    const saved = localStorage.getItem('esdl-auto-refresh-interval');
    return saved !== null ? parseInt(saved) : 5;
  });
  
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const autoRefreshIntervalRef = useRef(null);
  const initialLoadRef = useRef(false);
  const lastPathRef = useRef(location.pathname);
  
  // Filter state
  const [filterOptions, setFilterOptions] = useState({
    regions: [],
    applications: [],
    timeRanges: [
      { value: '10m', label: 'Last 10 Minutes' },
      { value: '30m', label: 'Last 30 Minutes' },
      { value: '1h', label: 'Last Hour' },
      { value: '6h', label: 'Last 6 Hours' },
      { value: '24h', label: 'Last 24 Hours' },
      { value: '7d', label: 'Last 7 Days' },
      { value: 'custom', label: 'Custom Range' }
    ]
  });
  
  const [filters, setFilters] = useState({
    region: '',
    application: '',
    timeRange: '10m',
    customMinutes: 10
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(() => {
    try {
      const stored = sessionStorage.getItem('esdl-last-refresh');
      return stored ? new Date(parseInt(stored)) : new Date();
    } catch (e) {
      console.warn('Error loading last refresh time from sessionStorage:', e);
      return new Date();
    }
  });

  // Add refs to track state and prevent duplicate requests
  const requestTracker = useRef({});
  const throttledRequests = useRef({});
  const isRequestPending = useRef(false);
  const previousLastRefreshRef = useRef(null);
  const notificationChecksRef = useRef(0);

  // Use our custom hook for ESDL notifications, but control when it updates
  const esdlNotifications = useEsdlNotifications(dashboardData, resourceStats);
  
  // Use a ref to track when notifications were last checked
  const lastNotificationCheckRef = useRef(0);

  // Function to manually check notifications with throttling
  const checkNotifications = useCallback(() => {
    const now = Date.now();
    // Only check once every 15 seconds max
    if (now - lastNotificationCheckRef.current < 15000) {
      console.log('Skipping notification check - too soon since last check');
      return;
    }
    
    if (esdlNotifications && esdlNotifications.updateNotifications) {
      console.log('Manually triggering notification check');
      esdlNotifications.updateNotifications();
      lastNotificationCheckRef.current = now;
    }
  }, [esdlNotifications]);

  // Add a function to centrally calculate and sync critical host counts
  const calculateCriticalCounts = (hosts) => {
    if (!hosts || !Array.isArray(hosts) || hosts.length === 0) {
      return {
        healthy: 0,
        warning: 0,
        critical: 0,
        criticalHosts: {
          cpu: [],
          memory: [],
          disk: []
        }
      };
    }

    // Initialize counters
    let healthyCount = 0;
    let warningCount = 0;
    let criticalCount = 0;
    
    // Initialize lists of critical hosts by metric
    const criticalHosts = {
      cpu: [],
      memory: [],
      disk: []
    };
    
    // Categorize each host based on its metrics
    hosts.forEach(host => {
      const cpuLoad = host.system?.cpu?.load_1min || 0;
      const memoryPercent = host.system?.memory?.percent || 0;
      const diskPercent = host.system?.disk?.percent || 0;
      
      // Check if host is critical for any metric
      const isCpuCritical = cpuLoad > 80;
      const isMemoryCritical = memoryPercent > 85;
      const isDiskCritical = diskPercent > 90;
      
      if (isCpuCritical) {
        criticalHosts.cpu.push(host.hostname);
      }
      
      if (isMemoryCritical) {
        criticalHosts.memory.push(host.hostname);
      }
      
      if (isDiskCritical) {
        criticalHosts.disk.push(host.hostname);
      }
      
      // Count host in most severe category
      if (isCpuCritical || isMemoryCritical || isDiskCritical) {
        criticalCount++;
      } else if (cpuLoad > 60 || memoryPercent > 70 || diskPercent > 75) {
        warningCount++;
      } else {
        healthyCount++;
      }
    });
    
    return {
      healthy: healthyCount,
      warning: warningCount,
      critical: criticalCount,
      criticalHosts
    };
  };

  // Function to fetch filter options with throttling
  const fetchFilterOptions = useCallback(() => {
    // Check if this request is already in progress or was recently made
    if (requestTracker.current.filterOptions) {
      console.log('Filter options request already in progress or throttled');
      return Promise.resolve();
    }

    // Set request tracking flag
    requestTracker.current.filterOptions = true;

    // Create throttled version if it doesn't exist
    if (!throttledRequests.current.filterOptions) {
      throttledRequests.current.filterOptions = throttle(async () => {
        try {
          setLoading(prev => ({ ...prev, filters: true }));
      const response = await esdlService.getMonitoringStats();
          
          // Extract unique regions and applications
          const regions = response.regions || [];
          const applications = response.applications || [];
          
          setFilterOptions(prev => ({
            ...prev,
            regions,
            applications
          }));
          
          setLoading(prev => ({ ...prev, filters: false }));
          
          // Cache the response
          const cacheData = {
            data: { regions, applications },
            timestamp: Date.now()
          };
          
          try {
            localStorage.setItem('filter-options', JSON.stringify(cacheData));
          } catch (e) {
            console.warn('Failed to cache filter options:', e);
          }
    } catch (error) {
      console.error('Error fetching filter options:', error);
          setError('Failed to load filter options. Please try again.');
          setLoading(prev => ({ ...prev, filters: false }));
        } finally {
          // Clear request tracking flag with delay to prevent immediate refetch
          setTimeout(() => {
            requestTracker.current.filterOptions = false;
          }, 5000);
        }
      }, 5000, { leading: true, trailing: false });
    }
    
    // Execute throttled function
    return throttledRequests.current.filterOptions();
  }, []);

  // Function to fetch dashboard data with throttling
  const fetchDashboardData = useCallback(async () => {
    // Check if this request is already in progress or was recently made
    if (requestTracker.current.dashboardData) {
      console.log('Dashboard data request already in progress or throttled');
      return Promise.resolve();
    }
    
    // Check if we're already loading this data
    if (loading.dashboard) return Promise.resolve();
    
    // Set request tracking flag
    requestTracker.current.dashboardData = true;
    
    try {
      setLoading(prev => ({ ...prev, dashboard: true }));
      
      // Check cache first
      const cacheKey = `dashboard-${filters.region}-${filters.application}-${filters.timeRange}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const cacheAge = Date.now() - timestamp;
          
          // Use cache if it's less than 2 minutes old
          if (cacheAge < 2 * 60 * 1000) {
            console.log('Using cached dashboard data...');
            if (data) {
              // Calculate consistent host counts
              if (data.hosts && Array.isArray(data.hosts)) {
                const counts = calculateCriticalCounts(data.hosts);
                data.host_counts = counts;
                
                // Ensure system_health reflects the same counts
                if (data.system_health) {
                  data.system_health.critical_hosts = counts.criticalHosts;
                }
              }
              
              setDashboardData(data);
              setLoading(prev => ({ ...prev, dashboard: false }));
              
              // Clear request tracking flag with delay
              setTimeout(() => {
                requestTracker.current.dashboardData = false;
              }, 5000);
              
              return Promise.resolve();
            }
          }
        } catch (error) {
          console.warn('Error parsing cached dashboard data:', error);
        }
      }
      
      // Prepare filter params
      const params = {};
      if (filters.region) params.region = filters.region;
      
      const response = await esdlService.getDashboardMetrics(params);
      
      // Calculate consistent host counts
      if (response.hosts && Array.isArray(response.hosts)) {
        const counts = calculateCriticalCounts(response.hosts);
        response.host_counts = counts;
        
        // Ensure system_health reflects the same counts
        if (response.system_health) {
          response.system_health.critical_hosts = counts.criticalHosts;
        }
      }
      
      setDashboardData(response);
      
      // No need for explicit check here, our hook will handle it
      
      // Cache the response
      const cacheData = {
        data: response,
        timestamp: Date.now()
      };
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (e) {
        console.warn('Failed to cache dashboard data:', e);
      }
      
      setLoading(prev => ({ ...prev, dashboard: false }));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
      setLoading(prev => ({ ...prev, dashboard: false }));
    } finally {
      // Clear request tracking flag with delay
      setTimeout(() => {
        requestTracker.current.dashboardData = false;
      }, 5000);
    }
  }, [filters.region, filters.application, filters.timeRange, loading.dashboard]);

  // Prepare filter params for API calls with custom minutes handling
  const getTimeRangeParam = useCallback(() => {
    if (filters.timeRange === 'custom') {
      return `${filters.customMinutes}m`;
    }
    return filters.timeRange;
  }, [filters.timeRange, filters.customMinutes]);

  // Function to fetch event stats with throttling
  const fetchEventStats = useCallback(async () => {
    // Check if this request is already in progress or was recently made
    if (requestTracker.current.eventStats) {
      console.log('Event stats request already in progress or throttled');
      return Promise.resolve();
    }
    
    // Check if we're already loading this data
    if (loading.events) return Promise.resolve();
    
    // Set request tracking flag
    requestTracker.current.eventStats = true;
    
    try {
      setLoading(prev => ({ ...prev, events: true }));
      
      // Check cache first
      const cacheKey = `event-stats-${filters.region}-${filters.application}-${filters.timeRange}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const cacheAge = Date.now() - timestamp;
          
          // Use cache if it's less than 2 minutes old
          if (cacheAge < 2 * 60 * 1000) {
            console.log('Using cached event stats data...');
            if (data) {
              setEventStats(data);
              setLoading(prev => ({ ...prev, events: false }));
              
              // Clear request tracking flag with delay
              setTimeout(() => {
                requestTracker.current.eventStats = false;
              }, 5000);
              
              return Promise.resolve();
            }
          }
        } catch (error) {
          console.warn('Error parsing cached event stats:', error);
        }
      }
      
      // Prepare filter params
      const params = {
        range: getTimeRangeParam()
      };
      if (filters.region) params.region = filters.region;
      if (filters.application) params.application = filters.application;
      
      const response = await esdlService.getEventStats(params);
      
      // If we got no data, fill with mock data for demonstration
      const eventData = (!response || !response.total_events || 
        (response.total_events.received === 0 && 
         response.total_events.processed === 0 && 
         response.total_events.dropped === 0)) 
        ? {
            total_events: {
              received: 1971800,
              processed: 1657500,
              dropped: 302300
            },
            performance: {
              processing_efficiency: 84.1,
              drop_rate: 15.9
            },
            region_stats: {
              "us-west-1": { received: 865400, processed: 742800, dropped: 122600 },
              "eu-central-1": { received: 562500, processed: 489700, dropped: 72800 },
              "ap-south-1": { received: 543900, processed: 425000, dropped: 106900 }
            },
            time_range: filters.timeRange
          }
        : response;
      
      setEventStats(eventData);
      
      // Cache the response
      const cacheData = {
        data: eventData,
        timestamp: Date.now()
      };
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (e) {
        console.warn('Failed to cache event stats:', e);
      }
      
      setLoading(prev => ({ ...prev, events: false }));
    } catch (error) {
      console.error('Error fetching event stats:', error);
      setError('Failed to load event statistics. Please try again.');
      
      // Set mock data on error
      const mockData = {
        total_events: {
          received: 1971800,
          processed: 1657500,
          dropped: 302300
        },
        performance: {
          processing_efficiency: 84.1,
          drop_rate: 15.9
        },
        region_stats: {
          "us-west-1": { received: 865400, processed: 742800, dropped: 122600 },
          "eu-central-1": { received: 562500, processed: 489700, dropped: 72800 },
          "ap-south-1": { received: 543900, processed: 425000, dropped: 106900 }
        },
        time_range: filters.timeRange
      };
      
      setEventStats(mockData);
      
      // Cache the mock data on error to prevent repeated failed requests
      const cacheData = {
        data: mockData,
        timestamp: Date.now()
      };
      try {
        localStorage.setItem(`event-stats-${filters.region}-${filters.application}-${filters.timeRange}`, JSON.stringify(cacheData));
      } catch (e) {
        console.warn('Failed to cache mock event stats:', e);
      }
      
      setLoading(prev => ({ ...prev, events: false }));
    } finally {
      // Clear request tracking flag with delay
      setTimeout(() => {
        requestTracker.current.eventStats = false;
      }, 5000);
    }
  }, [filters.region, filters.application, filters.timeRange, filters.customMinutes, loading.events, getTimeRangeParam]);

  // Function to fetch resource stats with throttling
  const fetchResourceStats = useCallback(async () => {
    // Check if this request is already in progress or was recently made
    if (requestTracker.current.resourceStats) {
      console.log('Resource stats request already in progress or throttled');
      return Promise.resolve();
    }
    
    // Check if we're already loading this data
    if (loading.resources) return Promise.resolve();
    
    // Set request tracking flag
    requestTracker.current.resourceStats = true;
    
    try {
      setLoading(prev => ({ ...prev, resources: true }));
      
      // Check cache first
      const cacheKey = `resource-stats-${filters.region}-${filters.timeRange}`;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const cacheAge = Date.now() - timestamp;
          
          // Use cache if it's less than 2 minutes old
          if (cacheAge < 2 * 60 * 1000) {
            console.log('Using cached resource stats data...');
            if (data) {
              setResourceStats(data);
              
              // Also update dashboardData with the cached system health data if needed
              if (dashboardData && data.summary) {
                console.log('Updating system health from cached resource stats');
                setDashboardData(prevData => ({
                  ...prevData,
                  system_health: {
                    ...prevData?.system_health,
                    avg_cpu_load: data.summary.avg_cpu_load,
                    avg_memory_percent: data.summary.avg_memory_percent,
                    avg_disk_percent: data.summary.avg_disk_percent,
                    critical_hosts: data.summary.critical_hosts
                  }
                }));
              }
              
              setLoading(prev => ({ ...prev, resources: false }));
              
              // Clear request tracking flag with delay
              setTimeout(() => {
                requestTracker.current.resourceStats = false;
              }, 5000);
              
              return Promise.resolve();
            }
          }
        } catch (error) {
          console.warn('Error parsing cached resource stats:', error);
        }
      }
      
      // Prepare filter params
      const params = {
        range: getTimeRangeParam()
      };
      if (filters.region) params.region = filters.region;
      
      console.log('Calling getSystemResources with params:', params);
      const response = await esdlService.getSystemResources(params);
      console.log('Received system resources response:', response);
      
      // If no data or empty hosts array, provide sample data only for development
      const resourceData = (!response || !response.hosts || response.hosts.length === 0)
        ? { hosts: [], summary: { avg_cpu_load: 0, avg_memory_percent: 0, avg_disk_percent: 0, critical_hosts: {cpu: [], memory: [], disk: []} } }
        : response;
      
      console.log('Setting resource stats with data:', resourceData);
      setResourceStats(resourceData);
      
      // No need for explicit check here, our hook will handle notifications
      
      // Update dashboard data with system resource information to ensure consistency
      if (dashboardData && dashboardData.hosts) {
        // Update the host data to include the system metrics
        const updatedHosts = (dashboardData.hosts || []).map(host => {
          const resourceHost = resourceData.hosts?.find(h => h.hostname === host.hostname);
          if (resourceHost) {
            return {
              ...host,
              system: {
                ...host.system,
                cpu: {
                  ...host.system?.cpu,
                  load_1min: resourceHost.cpu_load
                },
                memory: {
                  ...host.system?.memory,
                  percent: resourceHost.memory_percent
                },
                disk: {
                  ...host.system?.disk,
                  percent: resourceHost.disk_percent
                }
              }
            };
          }
          return host;
        });
        
        // Recalculate critical counts using our central function
        const counts = calculateCriticalCounts(updatedHosts);
        
        setDashboardData(prevData => {
          // Ensure the dashboard data reflects consistent critical host counts
          return {
            ...prevData,
            hosts: updatedHosts,
            host_counts: counts,
            system_health: {
              ...prevData?.system_health,
              avg_cpu_load: resourceData.summary?.avg_cpu_load || 0,
              avg_memory_percent: resourceData.summary?.avg_memory_percent || 0,
              avg_disk_percent: resourceData.summary?.avg_disk_percent || 0,
              critical_hosts: counts.criticalHosts
            }
          };
        });
      }
      
      // Cache the response
      const cacheData = {
        data: resourceData,
        timestamp: Date.now()
      };
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (e) {
        console.warn('Failed to cache resource stats:', e);
      }
      
      setLoading(prev => ({ ...prev, resources: false }));
    } catch (error) {
      console.error('Error fetching resource stats:', error);
      setError('Failed to load system resource data. Please try again.');
      
      // Set mock data on error
      const mockData = {
        hosts: [
          { hostname: 'server-01', region: 'us-west-1', cpu_load: 42.5, memory_percent: 67.8, disk_percent: 73.2 },
          { hostname: 'server-02', region: 'us-west-1', cpu_load: 32.1, memory_percent: 58.4, disk_percent: 65.5 },
          { hostname: 'server-03', region: 'eu-central-1', cpu_load: 78.9, memory_percent: 82.3, disk_percent: 91.7 },
          { hostname: 'server-04', region: 'eu-central-1', cpu_load: 25.6, memory_percent: 45.2, disk_percent: 59.8 },
          { hostname: 'server-05', region: 'ap-south-1', cpu_load: 53.3, memory_percent: 72.1, disk_percent: 85.4 }
        ],
        summary: {
          avg_cpu_load: 46.5,
          avg_memory_percent: 65.2,
          avg_disk_percent: 75.1,
          critical_hosts: {
            cpu: ['server-03'],
            memory: ['server-03'],
            disk: ['server-03', 'server-05']
          }
        },
        host_count: 5,
        time_range: filters.timeRange
      };
      
      setResourceStats(mockData);
      
      // Also update dashboardData with the mock system health data
      console.log('Setting mock system health data due to error');
      setDashboardData(prevData => ({
        ...prevData,
        system_health: {
          ...prevData?.system_health,
          avg_cpu_load: 46.5,
          avg_memory_percent: 65.2,
          avg_disk_percent: 75.1,
          critical_hosts: {
            cpu: ['server-03'],
            memory: ['server-03'],
            disk: ['server-03', 'server-05']
          }
        }
      }));
      
      // Cache the mock data
      const cacheData = {
        data: mockData,
        timestamp: Date.now()
      };
      try {
        localStorage.setItem(`resource-stats-${filters.region}-${filters.timeRange}`, JSON.stringify(cacheData));
      } catch (e) {
        console.warn('Failed to cache mock resource stats:', e);
      }
      
      setLoading(prev => ({ ...prev, resources: false }));
    } finally {
      // Clear request tracking flag with delay
      setTimeout(() => {
        requestTracker.current.resourceStats = false;
      }, 5000);
    }
  }, [filters.region, filters.timeRange, filters.customMinutes, dashboardData, loading.resources, setError, esdlService, getTimeRangeParam]);

  // Prevent multiple simultaneous requests by batching them
  const fetchAllData = useCallback(async () => {
    if (isRequestPending.current) {
      console.log('A batch request is already in progress');
      return;
    }
    
    isRequestPending.current = true;
    
    try {
      // Load cached filter options on startup
      const cachedFilterOptions = localStorage.getItem('filter-options');
      if (cachedFilterOptions) {
        try {
          const { data, timestamp } = JSON.parse(cachedFilterOptions);
          const cacheAge = Date.now() - timestamp;
          
          if (cacheAge < 10 * 60 * 1000) { // 10 minutes cache for filter options
            console.log('Using cached filter options...');
            if (data.regions && data.applications) {
              setFilterOptions(prev => ({
      ...prev,
                regions: data.regions,
                applications: data.applications
              }));
            }
          }
        } catch (error) {
          console.warn('Error parsing cached filter options:', error);
        }
      }
      
      // First get the dashboard data, then update with resource stats
      // This prevents race conditions where the data is not properly integrated
      try {
        await fetchFilterOptions();
        await fetchDashboardData();
        
        // Get the resource and event stats after dashboard data is loaded
        const resourcePromise = fetchResourceStats();
        const eventsPromise = fetchEventStats();
        
        // Wait for both to complete
        await Promise.all([resourcePromise, eventsPromise]);
        
        // After all data is loaded, save to sessionStorage for persistent state
        // between page navigations
        try {
          sessionStorage.setItem('esdl-dashboard-data', JSON.stringify(dashboardData));
          sessionStorage.setItem('esdl-event-stats', JSON.stringify(eventStats));
          sessionStorage.setItem('esdl-resource-stats', JSON.stringify(resourceStats));
          sessionStorage.setItem('esdl-last-refresh', Date.now().toString());
          console.log('Dashboard data saved to sessionStorage');
          
          // Do a single notification check after initial load - with delay to ensure the state is updated
          setTimeout(() => checkNotifications(), 2000);
        } catch (e) {
          console.warn('Failed to save dashboard data to sessionStorage:', e);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      }
      
      setLastRefresh(new Date());
    } finally {
      isRequestPending.current = false;
    }
  }, [fetchFilterOptions, fetchDashboardData, fetchEventStats, fetchResourceStats, dashboardData, eventStats, resourceStats, esdlNotifications, checkNotifications]);

  // Load all data on initial render and handle auto-refresh setup
  useEffect(() => {
    // Function to set up auto-refresh interval only once
    const setupAutoRefresh = () => {
      console.log(`Setting up auto-refresh every ${autoRefreshInterval} minutes`);
      
      // Clear any existing interval before setting a new one
      if (autoRefreshIntervalRef.current) {
        console.log('Clearing existing auto-refresh interval');
        clearInterval(autoRefreshIntervalRef.current);
      }
      
      // Only set up interval if auto-refresh is enabled
      if (autoRefreshEnabled) {
        autoRefreshIntervalRef.current = setInterval(() => {
          console.log('Auto-refresh timer triggered');
          // Only refresh if we're not already refreshing
          if (!isRequestPending.current) {
            fetchAllData();
          } else {
            console.log('Skipping auto-refresh as a request is already in progress');
          }
        }, autoRefreshInterval * 60 * 1000);
        
        return () => {
          if (autoRefreshIntervalRef.current) {
            clearInterval(autoRefreshIntervalRef.current);
            autoRefreshIntervalRef.current = null;
          }
        };
      }
    };
    
    // Clear interval when component unmounts or dependencies change
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [autoRefreshEnabled, autoRefreshInterval, fetchAllData]);
  
  // Separate effect to handle initial load and navigation
  useEffect(() => {
    // Check if this is the first load or a navigation back to this page
    if (!initialLoadRef.current) {
      // First time loading - try session storage first, then API
      const hasSessionData = loadSessionData();
      if (!hasSessionData) {
        console.log('No saved dashboard data found, fetching fresh data');
        fetchAllData();
      }
      initialLoadRef.current = true;
      
      // Set up auto-refresh after initial load
      if (autoRefreshEnabled) {
        console.log(`Setting up auto-refresh every ${autoRefreshInterval} minutes`);
        autoRefreshIntervalRef.current = setInterval(() => {
          console.log('Auto-refresh timer triggered');
          fetchAllData();
        }, autoRefreshInterval * 60 * 1000);
      }
    } else if (lastPathRef.current !== location.pathname && location.pathname === '/esdl-monitoring') {
      // Only run this effect if we've navigated back to this page
      console.log('Detected navigation back to ESDL dashboard');
      
      // Check if data is stale (>30 seconds)
      const timeSinceLastRefresh = new Date() - lastRefresh;
      if (timeSinceLastRefresh > 30 * 1000) {
        console.log('Data is stale, refreshing on return to dashboard');
        // Only refresh if we're not already refreshing
        if (!isRequestPending.current) {
          fetchAllData();
        } else {
          console.log('Skipping refresh as a request is already in progress');
        }
      } else {
        console.log('Using existing data on return to dashboard, last refresh was', Math.round(timeSinceLastRefresh / 1000), 'seconds ago');
      }
    }
    
    // Update the lastPathRef to the current path
    lastPathRef.current = location.pathname;
    
    // Cleanup on unmount
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [location.pathname, fetchAllData, lastRefresh, autoRefreshEnabled, autoRefreshInterval]);
  
  // Force a notification check when dashboard data or resource stats change
  useEffect(() => {
    // Use a flag and debounce to only log this message once in a while
    const now = Date.now();
    
    // Skip logging if we've logged recently
    if (notificationChecksRef.current && now - notificationChecksRef.current < 10000) {
      return;
    }
    
    // Update the timestamp
    notificationChecksRef.current = now;
    
    // Only check if both dashboardData and resourceStats are available
    if (dashboardData && resourceStats && esdlNotifications) {
      console.log('Critical data updated, checking for notifications (debounced)...');
    }
  }, [dashboardData, resourceStats, esdlNotifications]);

  // Add effect to run notification check after auto-refresh happens
  useEffect(() => {
    // Skip the first render if previousLastRefreshRef is not yet set
    if (previousLastRefreshRef.current === null) {
      previousLastRefreshRef.current = lastRefresh;
      return;
    }
    
    if (previousLastRefreshRef.current !== lastRefresh && initialLoadRef.current) {
      console.log('Auto-refresh detected, scheduling notification check');
      // Wait a bit for state to settle before checking notifications
      setTimeout(() => checkNotifications(), 3000);
    }
    
    previousLastRefreshRef.current = lastRefresh;
  }, [lastRefresh, checkNotifications]);

  // Settings dialog functions
  const handleOpenSettings = () => {
    setSettingsDialogOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsDialogOpen(false);
  };

  // Handle auto-refresh toggle
  const handleAutoRefreshToggle = () => {
    setAutoRefreshEnabled(prev => !prev);
  };

  // Handle auto-refresh interval change
  const handleIntervalChange = (event) => {
    setAutoRefreshInterval(parseInt(event.target.value));
  };

  // Handle refresh button click - throttled to prevent spam
  const handleRefresh = useCallback(
    throttle(() => {
      console.log('Manual refresh triggered');
      fetchAllData().then(() => {
        // Manually trigger notification check after data refresh with a small delay
        setTimeout(() => checkNotifications(), 1000);
      }).catch(err => {
        console.error('Error during refresh:', err);
      });
    }, 5000, { leading: true, trailing: false }),
    [fetchAllData, checkNotifications]
  );
  
  // Format for auto-refresh status display
  const getNextRefreshTime = () => {
    if (!autoRefreshEnabled) return null;
    
    const nextRefreshDate = new Date(lastRefresh.getTime() + autoRefreshInterval * 60 * 1000);
    const now = new Date();
    const minutesRemaining = Math.floor((nextRefreshDate - now) / (60 * 1000));
    const secondsRemaining = Math.floor(((nextRefreshDate - now) % (60 * 1000)) / 1000);
    
    if (minutesRemaining < 0 || secondsRemaining < 0) return 'Refreshing soon...';
    return `Next refresh in ${minutesRemaining}m ${secondsRemaining}s`;
  };
  
  // Create debounced filter change handler
  const debouncedFilterChange = useCallback(
    debounce((name, value) => {
      setFilters(prev => {
        // If changing timeRange to custom, keep the current customMinutes
        if (name === 'timeRange' && value === 'custom') {
          return { ...prev, [name]: value };
        }
        
        return { ...prev, [name]: value };
      });
    }, 500), // 500ms debounce delay
    []
  );

  // Handle filter change with debounce to prevent multiple API calls
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    
    // For region changes, update immediately without debounce
    if (name === 'region') {
      setFilters(prev => ({ ...prev, [name]: value }));
      // Immediately fetch new data
      fetchDashboardData();
      fetchEventStats();
      fetchResourceStats();
    } else {
      // For other filters, use debounce
      debouncedFilterChange(name, value);
    }
  };

  // Handle region selection from EsdlStatsSummary component
  const handleRegionSelect = (region) => {
    // Update the region filter
    setFilters(prev => ({ ...prev, region }));
    // Immediately fetch new data
    fetchDashboardData();
    fetchEventStats();
    fetchResourceStats();
  };

  // Handle custom minutes change
  const handleCustomMinutesChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setFilters(prev => ({ ...prev, customMinutes: value }));
      // If we're already in custom mode, update the filter as well
      if (filters.timeRange === 'custom') {
        debouncedFilterChange('timeRange', 'custom');
      }
    }
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      region: '',
      application: '',
      timeRange: '10m',
      customMinutes: 10
    });
    setSearchTerm('');
  };
  
  // Filter hosts from dashboard data based on search term
  const filteredHosts = dashboardData?.hosts?.filter(host => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      host.hostname?.toLowerCase().includes(term) ||
      host.region?.toLowerCase().includes(term) ||
      host.ip_address?.toLowerCase().includes(term)
    );
  }) || [];
  
  // Prepare chart data for event processing
  const prepareEventChartData = () => {
    if (!eventStats || !eventStats.total_events) {
      return {
        labels: ['Received', 'Processed', 'Dropped'],
        datasets: [
          {
            label: 'Events',
            data: [0, 0, 0],
            backgroundColor: [
              alpha(theme.palette.info.main, 0.7),
              alpha(theme.palette.success.main, 0.7),
              alpha(theme.palette.error.main, 0.7)
            ],
            borderColor: [
              theme.palette.info.main,
              theme.palette.success.main,
              theme.palette.error.main
            ],
            borderWidth: 1,
            hoverOffset: 4
          }
        ]
      };
    }
    
    const data = {
      labels: ['Received', 'Processed', 'Dropped'],
      datasets: [
        {
          label: 'Events',
          data: [
            eventStats.total_events.received || 0,
            eventStats.total_events.processed || 0,
            eventStats.total_events.dropped || 0
          ],
          backgroundColor: [
            alpha(theme.palette.info.main, 0.7),
            alpha(theme.palette.success.main, 0.7),
            alpha(theme.palette.error.main, 0.7)
          ],
          borderColor: [
            theme.palette.info.main,
            theme.palette.success.main,
            theme.palette.error.main
          ],
          borderWidth: 1,
          hoverOffset: 4
        }
      ]
    };
    
    return data;
  };
  
  // Prepare chart data for system resources
  const prepareResourceChartData = () => {
    if (!resourceStats || !resourceStats.hosts || resourceStats.hosts.length === 0) {
      return {
        labels: ['No Data'],
        datasets: [
          {
            label: 'CPU Load',
            data: [0],
            backgroundColor: alpha(theme.palette.primary.main, 0.7),
            borderColor: theme.palette.primary.main,
            borderWidth: 1
          },
          {
            label: 'Memory Usage (%)',
            data: [0],
            backgroundColor: alpha(theme.palette.info.main, 0.7),
            borderColor: theme.palette.info.main,
            borderWidth: 1
          },
          {
            label: 'Disk Usage (%)',
            data: [0],
            backgroundColor: alpha(theme.palette.warning.main, 0.7),
            borderColor: theme.palette.warning.main,
            borderWidth: 1
          }
        ]
      };
    }
    
    // Get labels (hostnames) and values for CPU, memory, and disk usage
    // Limit to first 5 hosts to not overcrowd the chart
    const displayHosts = resourceStats.hosts.slice(0, 5);
    const hostnames = displayHosts.map(host => host.hostname);
    const cpuData = displayHosts.map(host => host.cpu_load || 0);
    const memoryData = displayHosts.map(host => host.memory_percent || 0);
    const diskData = displayHosts.map(host => host.disk_percent || 0);
    
    const data = {
      labels: hostnames,
      datasets: [
        {
          label: 'CPU Load',
          data: cpuData,
          backgroundColor: alpha(theme.palette.primary.main, 0.7),
          borderColor: theme.palette.primary.main,
          borderWidth: 1
        },
        {
          label: 'Memory Usage (%)',
          data: memoryData,
          backgroundColor: alpha(theme.palette.info.main, 0.7),
          borderColor: theme.palette.info.main,
          borderWidth: 1
        },
        {
          label: 'Disk Usage (%)',
          data: diskData,
          backgroundColor: alpha(theme.palette.warning.main, 0.7),
          borderColor: theme.palette.warning.main,
          borderWidth: 1
        }
      ]
    };
    
    return data;
  };

  // Track navigation changes to detect when user returns to this page from another route
  useEffect(() => {
    // Only run this effect if we've already had our initial load
    if (initialLoadRef.current && location.pathname === '/esdl-monitoring' && lastPathRef.current !== location.pathname) {
      console.log('Detected navigation back to ESDL dashboard');
      
      // Immediately update the dashboard UI with current cached data from sessionStorage
      try {
        const storedDashboardData = sessionStorage.getItem('esdl-dashboard-data');
        const storedEventStats = sessionStorage.getItem('esdl-event-stats');
        const storedResourceStats = sessionStorage.getItem('esdl-resource-stats');
        const storedLastRefresh = sessionStorage.getItem('esdl-last-refresh');
        
        if (storedDashboardData) setDashboardData(JSON.parse(storedDashboardData));
        if (storedEventStats) setEventStats(JSON.parse(storedEventStats));
        if (storedResourceStats) setResourceStats(JSON.parse(storedResourceStats));
        if (storedLastRefresh) setLastRefresh(new Date(parseInt(storedLastRefresh)));
      } catch (e) {
        console.warn('Error loading data from sessionStorage during navigation:', e);
      }
      
      // Check if data is stale (older than 30 seconds)
      const timeSinceLastRefresh = new Date() - lastRefresh;
      if (timeSinceLastRefresh > 30 * 1000) {
        console.log('Data is stale, refreshing on return to dashboard after navigation');
        // Use setTimeout to ensure the UI updates first with cached data
        setTimeout(() => fetchAllData(), 100);
      }
    }
    
    // Update the lastPathRef to the current path
    lastPathRef.current = location.pathname;
  }, [location.pathname, lastRefresh, fetchAllData]);

  // Sync current state to sessionStorage whenever key data changes
  useEffect(() => {
    if (dashboardData && eventStats && resourceStats) {
      try {
        sessionStorage.setItem('esdl-dashboard-data', JSON.stringify(dashboardData));
        sessionStorage.setItem('esdl-event-stats', JSON.stringify(eventStats));
        sessionStorage.setItem('esdl-resource-stats', JSON.stringify(resourceStats));
        sessionStorage.setItem('esdl-last-refresh', lastRefresh.getTime().toString());
        console.log('Dashboard data synced to sessionStorage');
      } catch (e) {
        console.warn('Failed to sync dashboard data to sessionStorage:', e);
      }
    }
  }, [dashboardData, eventStats, resourceStats, lastRefresh]);

  // Function to load session data - MOVED UP to fix initialization order
  const loadSessionData = useCallback(() => {
    try {
      const storedLastRefresh = sessionStorage.getItem('esdl-last-refresh');
      const storedDashboardData = sessionStorage.getItem('esdl-dashboard-data');
      const storedEventStats = sessionStorage.getItem('esdl-event-stats');
      const storedResourceStats = sessionStorage.getItem('esdl-resource-stats');
      
      if (storedLastRefresh && storedDashboardData && storedEventStats && storedResourceStats) {
        const lastRefreshTime = new Date(parseInt(storedLastRefresh));
        const timeSinceLastRefresh = new Date() - lastRefreshTime;
        console.log('Found saved dashboard data from', Math.round(timeSinceLastRefresh / 1000), 'seconds ago');
        
        // Use the stored data regardless of age for instant display
        setDashboardData(JSON.parse(storedDashboardData));
        setEventStats(JSON.parse(storedEventStats));
        setResourceStats(JSON.parse(storedResourceStats));
        setLastRefresh(lastRefreshTime);
        
        // If data is older than 30 seconds, refresh in the background
        if (timeSinceLastRefresh > 30 * 1000) {
          console.log('Saved data is older than 30 seconds, refreshing in background');
          setTimeout(() => {
            if (!isRequestPending.current) {
              fetchAllData();
            }
          }, 100); // Small delay to prevent UI blocking
        }
        
        return true;
      }
      return false;
    } catch (e) {
      console.warn('Failed to load dashboard data from sessionStorage:', e);
      return false;
    }
  }, []);  // Remove circular dependencies
  
  // Add extra effect for handling tab switching events
  useEffect(() => {
    // Add event listener for switching to errors tab
    const handleSwitchToErrorsTab = (event) => {
      // Switch to errors tab
      setActiveTab(2);
      
      // Ensure the region filter is set
      if (event.detail && event.detail.region) {
        setFilters(prev => ({ ...prev, region: event.detail.region }));
      }
    };
    
    // Register event listener
    window.addEventListener('switch-to-errors-tab', handleSwitchToErrorsTab);
    
    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('switch-to-errors-tab', handleSwitchToErrorsTab);
    };
  }, []);
  
  // Save auto-refresh settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('esdl-auto-refresh-enabled', JSON.stringify(autoRefreshEnabled));
  }, [autoRefreshEnabled]);

  useEffect(() => {
    localStorage.setItem('esdl-auto-refresh-interval', autoRefreshInterval.toString());
  }, [autoRefreshInterval]);
  
  // Add a timer to update the refresh countdown
  const [, setRefreshTimer] = useState(0);
  useEffect(() => {
    if (!autoRefreshEnabled) return;
    
    // Update the timer every second to show accurate countdown
    const timerId = setInterval(() => {
      setRefreshTimer(prev => prev + 1); // Just to trigger re-render
    }, 1000);
    
    return () => clearInterval(timerId);
  }, [autoRefreshEnabled, lastRefresh, autoRefreshInterval]);

  // Update instances when filters change
  useEffect(() => {
    // Don't fetch on initial render with empty filters
    if (!initialLoadRef.current) return;

    console.log('Filters changed, fetching new data:', filters);
    
    // Fetch all data when filters change
    fetchDashboardData();
    fetchEventStats();
    fetchResourceStats();
    
    // Update URL to reflect filter changes if needed
    const searchParams = new URLSearchParams();
    if (filters.region) searchParams.set('region', filters.region);
    if (filters.application) searchParams.set('application', filters.application);
    if (filters.timeRange) searchParams.set('timeRange', filters.timeRange);
    
    const newSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
    navigate(`${location.pathname}${newSearch}`, { replace: true });
  }, [filters.region, filters.application, filters.timeRange, filters.customMinutes]);

  return (
    <Container maxWidth="xl" sx={{ pb: 4 }}>
      <Box mb={4}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2
        }}>
          <Box>
            <Breadcrumbs aria-label="breadcrumb">
              <Link component={RouterLink} to="/" color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
                <DashboardIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                Dashboard
              </Link>
              <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                <CloudIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                ESDL Monitoring
              </Typography>
            </Breadcrumbs>
            <Typography 
              variant="h4" 
              component="h1" 
              fontWeight="bold" 
              sx={{ 
                mt: 1,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textFillColor: 'transparent'
              }}
            >
              ESDL Monitoring Dashboard
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={autoRefreshEnabled ? `Auto-refresh every ${autoRefreshInterval} minutes` : "Auto-refresh disabled"}>
              <IconButton
                color={autoRefreshEnabled ? "primary" : "default"}
                onClick={handleOpenSettings}
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                }}
              >
                <AutorenewIcon />
                {autoRefreshEnabled && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.success.main,
                      position: 'absolute',
                      top: 8,
                      right: 8,
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              disableElevation
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                py: 1
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
        
        {/* Auto-refresh Settings Dialog */}
        <Dialog
          open={settingsDialogOpen}
          onClose={handleCloseSettings}
          aria-labelledby="refresh-settings-dialog-title"
          PaperProps={{
            sx: {
              borderRadius: 2,
              width: 'min(400px, 95%)'
            }
          }}
        >
          <DialogTitle id="refresh-settings-dialog-title" sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center">
              <AutorenewIcon sx={{ mr: 1 }} />
              Auto-Refresh Settings
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRefreshEnabled}
                    onChange={handleAutoRefreshToggle}
                    color="primary"
                  />
                }
                label="Enable Auto-Refresh"
              />
            </Box>
            
            <FormControl component="fieldset" disabled={!autoRefreshEnabled}>
              <FormLabel component="legend">Refresh Interval</FormLabel>
              <RadioGroup
                value={autoRefreshInterval.toString()}
                onChange={handleIntervalChange}
                sx={{ mt: 1 }}
              >
                <FormControlLabel value="1" control={<Radio />} label="Every 1 minute" />
                <FormControlLabel value="5" control={<Radio />} label="Every 5 minutes" />
                <FormControlLabel value="10" control={<Radio />} label="Every 10 minutes" />
                <FormControlLabel value="30" control={<Radio />} label="Every 30 minutes" />
              </RadioGroup>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseSettings} variant="outlined">Close</Button>
          </DialogActions>
        </Dialog>
        
        {/* Inline Filter Bar (Similar to OCIInstances) */}
        {activeTab !== 3 && (
          <Paper
            elevation={0}
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backdropFilter: 'blur(8px)',
            }}
          >
            <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="region-select-label">Region</InputLabel>
                    <Select
                      labelId="region-select-label"
                      id="region-select"
                      name="region"
                      value={filters.region}
                      onChange={handleFilterChange}
                      label="Region"
                      startAdornment={
                        <RegionIcon sx={{ ml: 1, mr: 0.5, color: 'action.active', fontSize: '1.2rem' }} />
                      }
                    >
                      <MenuItem value="">All Regions</MenuItem>
                      {filterOptions.regions.map((region, index) => (
                        <MenuItem key={index} value={region}>{region}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="application-select-label">Application</InputLabel>
                    <Select
                      labelId="application-select-label"
                      id="application-select"
                      name="application"
                      value={filters.application}
                      onChange={handleFilterChange}
                      label="Application"
                      startAdornment={
                        <ServerIcon sx={{ ml: 1, mr: 0.5, color: 'action.active', fontSize: '1.2rem' }} />
                      }
                    >
                      <MenuItem value="">All Applications</MenuItem>
                      {filterOptions.applications.map((app, index) => (
                        <MenuItem key={index} value={app}>{app}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3} md={filters.timeRange === 'custom' ? 1.5 : 2}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="time-range-select-label">Time Range</InputLabel>
                    <Select
                      labelId="time-range-select-label"
                      id="time-range-select"
                      name="timeRange"
                      value={filters.timeRange}
                      onChange={handleFilterChange}
                      label="Time Range"
                      startAdornment={
                        <DateRangeIcon sx={{ ml: 1, mr: 0.5, color: 'action.active', fontSize: '1.2rem' }} />
                      }
                      sx={{ 
                        borderRadius: 3,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.divider, 0.3)
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: 3,
                            backgroundImage: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.95)})`,
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.1)}`
                          }
                        }
                      }}
                    >
                      {filterOptions.timeRanges.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {filters.timeRange === 'custom' && (
                  <Grid item xs={12} sm={3} md={1.5}>
                    <FormControl fullWidth size="small">
                      <TextField
                        label="Minutes"
                        type="number"
                        size="small"
                        value={filters.customMinutes}
                        onChange={handleCustomMinutesChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <TimerOutlined sx={{ color: 'action.active', fontSize: '1.2rem' }} />
                            </InputAdornment>
                          ),
                          inputProps: { min: 1 }
                        }}
                        sx={{ 
                          borderRadius: 3,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.divider, 0.3)
                          }
                        }}
                      />
                    </FormControl>
                  </Grid>
                )}
              
                <Grid item xs={12} sm={3} md={2}>
                  <Button 
                    size="small" 
                    onClick={handleResetFilters}
                    startIcon={<FilterIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    Reset Filters
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <OutlinedInput
                      placeholder="Search hosts..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      }
                      endAdornment={
                        searchTerm ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="clear search"
                              onClick={() => setSearchTerm('')}
                              edge="end"
                              size="small" 
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null
                      }
                      sx={{ borderRadius: 2 }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Toolbar>
          </Paper>
        )}
        
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap', 
          gap: 1, 
          mb: 2 
        }}>
          <Typography variant="body2" color="text.secondary">
            Last updated: {lastRefresh.toLocaleTimeString()} 
            {autoRefreshEnabled && (
              <>
                | <Chip 
                    size="small" 
                    icon={<AutorenewIcon fontSize="small" />} 
                    label={getNextRefreshTime()} 
                    variant="outlined"
                    sx={{ 
                      height: 20, 
                      '& .MuiChip-label': { px: 1, py: 0 },
                      '& .MuiChip-icon': { ml: 0.5 }
                    }}
                  />
              </>
            )}
            {filters.region ? ` | Region: ${filters.region}` : ''} 
            | Showing {filteredHosts.length} hosts
          </Typography>
        </Box>
        
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            indicatorColor="primary"
            textColor="primary"
            sx={{
              '& .MuiTab-root': {
                minWidth: 80,
                textTransform: 'none',
                fontWeight: 'medium'
              }
            }}
          >
            <Tab label="All Hosts" />
            <Tab label="Critical" />
            <Tab label="Error Logs" />
          </Tabs>
        </Box>
        
        {activeTab === 0 && (
          <>
            <EsdlStatsSummary 
              data={{
                ...dashboardData,
                host_counts: dashboardData?.host_counts || calculateCriticalCounts(dashboardData?.hosts || [])
              }} 
              loading={loading.dashboard}
              onRegionSelect={handleRegionSelect}
            />
            
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6}>
                <EsdlSummaryChart
                  title="Event Processing"
                  subtitle="Total events received, processed, and dropped"
                  chartType="doughnut"
                  chartData={prepareEventChartData()}
                  loading={loading.events}
                  onRefresh={() => fetchEventStats()}
                  height={280}
                  menuOptions={[
                    { 
                      label: 'View Details', 
                      action: () => {} 
                    },
                    { 
                      label: 'Export Data', 
                      action: () => {}
                    }
                  ]}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <EsdlSummaryChart
                  title="System Resources"
                  subtitle="CPU, Memory, and Disk utilization by host"
                  chartType="bar"
                  chartData={prepareResourceChartData()}
                  loading={loading.resources}
                  onRefresh={() => fetchResourceStats()}
                  height={280}
                  menuOptions={[
                    { 
                      label: 'View Details', 
                      action: () => {} 
                    },
                    { 
                      label: 'Export Data', 
                      action: () => {}
                    }
                  ]}
                />
              </Grid>
            </Grid>
            
            <Box id="hosts-section">
              <EsdlStatusGrid 
                hosts={filteredHosts} 
                loading={loading.dashboard} 
                onRefresh={handleRefresh}
              />
            </Box>
          </>
        )}
        
        {activeTab === 1 && (
          <EsdlStatusGrid 
            hosts={filteredHosts.filter(host => 
              host.system?.cpu?.load_1min > 80 || 
              host.system?.memory?.percent > 85 || 
              host.system?.disk?.percent > 90 ||
              (host.error_count && host.error_count > 0)
            )} 
            loading={loading.dashboard}
            onRefresh={handleRefresh}
          />
        )}
        
        {activeTab === 2 && (
          <ErrorLogsTable 
            region={filters.region}
            hostname=""
          />
        )}
      </Box>
    </Container>
  );
};

export default EsdlMonitoringDashboard; 